import {
  ForecastDay,
  ForecastEventInput,
  ForecastRowInput,
  LaidOutEvent,
  LaidOutRow,
} from '../../../contracts/FMForecastContracts';

const ONE_DAY = 24 * 60 * 60 * 1000;

/**
 * Pure, behavior-parity port of the GRM `fmCalendar` forecast-grid layout
 * (`fmteamcalendar.js` `getDays`/`fetchLines` + `fmdashboard.controller.js`
 * `getStartDateColumnIdx`/`getDateDiffInDays`). Kept side-effect free so it can be unit-tested
 * against fixtures and reused by `FMTeamCalendar` without the FXP shell.
 */

/** Build `numberOfDays` day columns starting at `startDate` (parity: `getDays`). */
export function getDays(startDate: Date, numberOfDays: number): ForecastDay[] {
  const days: ForecastDay[] = [];
  for (let i = 0; i < numberOfDays; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    days.push({ date: d });
  }
  return days;
}

/** Number of days in the month of `d` (parity: `getNumberOfdays`). */
export function getNumberOfDaysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/**
 * 1-based day-of-month column index for an event start (parity: `getStartDateColumnIdx`).
 * Clamps to `reqStartDate` when the event starts before the visible range.
 */
export function getStartDateColumnIdx(date?: Date, reqStartDate?: Date): number {
  if (!date) {
    return 0;
  }
  let d = date;
  if (reqStartDate && d <= reqStartDate) {
    d = reqStartDate;
  }
  return d.getDate();
}

/**
 * Inclusive day-count duration for an event (parity: `getDateDiffInDays`).
 * Clamps to [reqStartDate, reqEndDate], anchors start=08:00 / end=20:00, rounds.
 */
export function getDateDiffInDays(
  firstDate?: Date,
  secondDate?: Date,
  reqStartDate?: Date,
  reqEndDate?: Date
): number {
  if (!firstDate || !secondDate) {
    return 0;
  }
  let f = new Date(firstDate);
  let s = new Date(secondDate);
  if (reqStartDate && reqEndDate) {
    if (f <= reqStartDate) {
      f = new Date(reqStartDate);
    }
    if (s >= reqEndDate) {
      s = new Date(reqEndDate);
    }
  }
  f.setHours(8, 0, 0, 0);
  s.setHours(20, 0, 0, 0);
  const diffDays = Math.round(Math.abs((f.getTime() - s.getTime() + 1) / ONE_DAY));
  return diffDays + 1;
}

/**
 * Level-pack a resource row's events into non-overlapping lanes (parity: `fetchLines`).
 * Uses the same 32-bit bitmask overlap test; assigns each event the lowest free level,
 * positions it (top = 25*level px, width = 36*duration - 3 px) and returns the row height.
 */
export function computeRowLayout(
  events: ForecastEventInput[],
  numberOfDays: number
): { lines: LaidOutEvent[]; height: number } {
  const levelArray: number[] = new Array(100).fill(0);
  let maxLevel = 0;
  const lines: LaidOutEvent[] = [];

  for (const ev of events) {
    const startDateIndex = ev.startDateIndex;
    const duration = ev.duration;
    // 0-based column — parity of GRM `currentStartDateIndex = startDateIndex - 1`
    // (used in BOTH the bitmask shift and the placement column).
    const startColumnIndex = startDateIndex - 1;
    // eslint-disable-next-line no-bitwise
    const currentEvent =
      ((1 << duration) - 1) << (numberOfDays - startColumnIndex - duration);

    let level = -1;
    for (let levelMap = 0; levelMap < levelArray.length; levelMap++) {
      // eslint-disable-next-line no-bitwise
      if ((levelArray[levelMap] & currentEvent) === 0) {
        level = levelMap;
        // eslint-disable-next-line no-bitwise
        levelArray[levelMap] |= currentEvent;
        break;
      }
    }
    if (level === -1) {
      level = 100;
    }

    lines.push({
      ...ev,
      level,
      top: 25 * level,
      width: 36 * duration - 3,
      startColumnIndex,
    });
    maxLevel = maxLevel > level ? maxLevel : level;
  }

  return { lines, height: maxLevel * 25 + 30 };
}

/** Layout every row (parity: the per-row loop in `fetchLines`). */
export function layoutRows(
  rows: ForecastRowInput[],
  numberOfDays: number
): LaidOutRow[] {
  return rows.map((r) => {
    const { lines, height } = computeRowLayout(r.events, numberOfDays);
    const { events: _events, ...rest } = r;
    return { ...rest, lines, height };
  });
}
